import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ImageLightbox as PlasserImageLightbox } from "@plasser/ui";
import { useImageProxyUrls } from "@/hooks/useImageProxyUrl";
import { AuthenticatedImage } from "@/components/ui/AuthenticatedImage";
import {
  GalleryImageTile,
  GalleryPlaceholderTile,
  ImageGalleryGrid,
} from "@/components/ui/image-gallery-grid";

interface ImageLightboxProps {
  images: string[];
  initialIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * CHK-gebundener Wrapper (TES-2214) um die generische `@plasser/ui`
 * `ImageLightbox`-Huelle: liefert je Bild einen auth-gefetchten
 * `AuthenticatedImage`-Slot + die per i18n lokalisierten Label-Strings für
 * alt-Text und Indicator (TES-2303 — vorher hardcodiertes Deutsch, jetzt über
 * `useTranslation` für die aktive Locale aufgelöst). Die Bilder sind bereits
 * proxy-URLs (von den aufrufenden Komponenten aufgeloest) — wir reichen sie
 * direkt durch.
 */
export function ImageLightbox({
  images,
  initialIndex = 0,
  open,
  onOpenChange,
}: ImageLightboxProps) {
  const { t } = useTranslation();

  const slots = images.map((src, index) => (
    <AuthenticatedImage
      key={`${index}-${src}`}
      src={src}
      alt={t("common.imageAlt", { number: index + 1 })}
      className="max-h-[80vh] max-w-full object-contain"
      showSkeleton={true}
    />
  ));

  return (
    <PlasserImageLightbox
      images={slots}
      initialIndex={initialIndex}
      open={open}
      onOpenChange={onOpenChange}
      title={t("common.imageGallery")}
      closeLabel={t("common.close")}
      previousLabel={t("common.previous")}
      nextLabel={t("common.next")}
      indicatorLabel={(index) => t("common.imageAlt", { number: index + 1 })}
    />
  );
}

interface ImageThumbnailGridProps {
  images: string[];
  /** @deprecated TES-2000: Bilder rendern jetzt GROSS über die ImageGalleryGrid-Kacheln;
   *  die alte 48/64px-Thumbnail-Dichte existiert nicht mehr. Prop bleibt nur für
   *  Call-Site-Kompatibilität erhalten und wird ignoriert. */
  size?: "sm" | "md";
  /** Maximale Spaltenzahl auf grossen Screens (Default 4, adaptiv zur Bild-Anzahl). */
  columns?: 2 | 3 | 4;
}

/**
 * Grosse, responsive Bild-Galerie (TES-2000, vorher 48px-Thumbnails): Kacheln sind
 * `aspect-square` auf @plasser/ui `ImageGalleryGrid`-Basis und nutzen die verfügbare
 * Breite; Klick öffnet die Lightbox. Gleiche Optik wie PhotoEvidenceGrid — kein
 * zweiter Bildpfad (`useImageProxyUrls` → `AuthenticatedImage` → `ImageLightbox`).
 */
export function ImageThumbnailGrid({ images, columns = 4 }: ImageThumbnailGridProps) {
  const { t } = useTranslation();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Use proxy URLs for private bucket access (synchronous, no loading state)
  const proxyUrls = useImageProxyUrls(images);

  if (!images || images.length === 0) return null;

  const handleImageClick = (index: number) => {
    setSelectedIndex(index);
    setLightboxOpen(true);
  };

  // Filter valid URLs for display and lightbox
  const validProxyUrls = proxyUrls.filter((url): url is string => !!url);

  // TES-1708: Thumbnails und Lightbox MÜSSEN auf dieselbe (gefilterte) Liste indizieren.
  // Vorher übergab der Klick den Original-Index in die Lightbox, die aber nur die gültigen
  // URLs kennt → bei einer früheren null-URL öffnete sich das falsche Bild. validIndex =
  // Position dieser URL innerhalb von validProxyUrls (= Anzahl gültiger URLs davor).
  const tiles = proxyUrls.map((proxyUrl, index) => ({
    proxyUrl,
    validIndex: proxyUrl ? proxyUrls.slice(0, index).filter(Boolean).length : -1,
  }));

  return (
    <>
      <ImageGalleryGrid columns={columns}>
        {tiles.map(({ proxyUrl, validIndex }, index) =>
          proxyUrl ? (
            <GalleryImageTile
              key={index}
              image={
                <AuthenticatedImage
                  src={proxyUrl}
                  alt={t("common.imageAlt", { number: index + 1 })}
                  className="h-full w-full object-cover"
                />
              }
              onView={() => handleImageClick(validIndex)}
              viewLabel={t("common.viewImageLabel", { number: index + 1 })}
            />
          ) : (
            <GalleryPlaceholderTile key={index} />
          ),
        )}
      </ImageGalleryGrid>
      <ImageLightbox
        images={validProxyUrls}
        initialIndex={selectedIndex}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
      />
    </>
  );
}
